import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators';

import { selectAllRawPolicies } from '@amfam/policy/data-access/lib/+state/policies/policies.selectors';
import {
  selectAllPolicySummaries,
  selectCustomerGroupId,
  selectCustomerSegmentCd,
  selectPolicyAnalytics
} from '@amfam/policy/data-access/lib/+state/summaries/summaries.selectors';
import { PolicySummary } from '@amfam/policy/models/lib/models/policy-summary';
import {
  selectAccountType,
  selectAddress,
  selectAfeSessionId,
  selectTrackId
} from '@amfam/shared/user/lib/+state/user.selectors';
import { Applications } from '@amfam/shared/utility/shared-services/lib/application/application.enum';
import { ApplicationService } from '@amfam/shared/utility/shared-services/lib/application/application.service';
import { WindowRef } from '@amfam/shared/utility/shared-services/lib/window/window.service';

import { DataDogService } from '../services/data-dog.service';
import { GoogleAnalyticsService } from '../services/google-analytics.service';
import {
  loadDataDog as AnalyticsActionsLoadDataDog,
  loadGoogleAnalytics as AnalyticsActionsLoadGoogleAnalytics,
  sendAnalytic as AnalyticsActionsSendAnalytic,
  loadGoogleAnalyticsFailure,
  loadGoogleAnalyticsSuccess,
  storeGtmDetails
} from './analytics.actions';
import { selectGoogleAnalyticsLoaded, selectGtm } from './analytics.selectors';

export const loadGoogleAnalytics = createEffect(
  (actions$ = inject(Actions), googleAnalyticsService = inject(GoogleAnalyticsService)) => {
    return actions$.pipe(
      ofType(AnalyticsActionsLoadGoogleAnalytics),
      switchMap(() =>
        googleAnalyticsService.loadGoogleTagManagerScript().pipe(
          map(() => loadGoogleAnalyticsSuccess()),
          catchError(() => of(loadGoogleAnalyticsFailure()))
        )
      )
    );
  },
  { functional: true }
);

export const loadDataDog = createEffect(
  (actions$ = inject(Actions), datadogService = inject(DataDogService)) => {
    return actions$.pipe(
      ofType(AnalyticsActionsLoadDataDog),
      tap(() => datadogService.loadDataDog())
    );
  },
  { functional: true, dispatch: false }
);

export const sendAnalytic = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    googleAnalyticsService = inject(GoogleAnalyticsService),
    applicationService = inject(ApplicationService),
    datadogService = inject(DataDogService),
    deviceService = inject(DeviceDetectorService),
    windowRef = inject(WindowRef)
  ) => {
    return actions$.pipe(
      ofType(AnalyticsActionsSendAnalytic),
      map(action => action.options),
      concatLatestFrom(() => [
        store.select(selectGoogleAnalyticsLoaded),
        store.select(selectGtm),
        store.select(selectTrackId),
        store.select(selectAddress),
        store.select(selectAllRawPolicies),
        store.select(selectCustomerGroupId),
        store.select(selectCustomerSegmentCd),
        store.select(selectAllPolicySummaries),
        store.select(selectAfeSessionId),
        store.select(selectAccountType),
        store.select(selectPolicyAnalytics)
      ]),
      concatMap(
        ([
          options,
          googleEnabled,
          gtm,
          trackId,
          primaryAddress,
          policies,
          customerGroupId,
          customerSegmentCd,
          summaries,
          afeSessionId,
          accountType,
          policyAnalytics
        ]) => {
          const actions: Action[] = [];
          let policyPartnerId: string | undefined,
            sourceSystem: string | undefined,
            device: string | undefined,
            productType: string | undefined,
            productLine: string | undefined;
          let isWebview = false;
          const policyNumber = options.page?.policyNumber || options.event?.policyNumber;
          try {
            const summary = (summaries as PolicySummary[]).find(
              s => s.policyNumber === policyNumber
            );
            if (summary) {
              policyPartnerId = summary.policyPartnerId;
              sourceSystem = summary.sourcePath;
              productType = summary.policyTypeDisplayName;
              productLine = summary.productDescription;
            }
            device = deviceService.device();
            isWebview = windowRef.nativeWindow?.location?.pathname?.includes('webview') ?? false;
          } catch {}

          // send google analytic
          if (googleEnabled) {
            // send google analytic for page
            if (options?.page) {
              const atleastOnePolicyActive = policies.some(policy => policy.status === 'InForce');
              let type0002;
              // Determine type0002 only when user is loggedin else it will be undefined
              if (trackId) {
                type0002 = atleastOnePolicyActive ? 'amf001' : 'amf000';
              }

              const gtmDataLayer = {
                event: 'dataLayer-initialized',
                content_language: 'English',
                content_group: applicationService.isApp(Applications.MYACCOUNT_ADMIN)
                  ? 'myaccount-admin'
                  : 'myaccount',
                user_id: trackId,
                page_name: options.page.pageName,
                previous_page_name: gtm?.page_name,
                logged_in: trackId ? '1' : '0',
                type0001: 'amf0003',
                type0002: type0002,
                zip_code: primaryAddress?.zip5,
                city: primaryAddress?.city,
                state: primaryAddress?.state,
                dam_number: undefined,
                user: options.user,
                customer_group_id: customerGroupId,
                customer_segment_cd: customerSegmentCd,
                partner_id: policyPartnerId,
                policy_source_system: sourceSystem,
                partner_id_list: policyAnalytics?.partner_id_list,
                source_system_list: policyAnalytics?.source_system_list,
                afe_session_id: afeSessionId,
                device_platform: device,
                webview: isWebview,
                shared_component_flag: options.page.sharedComponentFlag
              };
              actions.push(storeGtmDetails({ payload: gtmDataLayer }));
              googleAnalyticsService.sendAnalytic(gtmDataLayer);
            } else {
              // send google analytic for event
              let eventWithAdditionalProps = {
                ...options.event,
                afe_session_id: afeSessionId,
                device_platform: device,
                webview: isWebview,
                customer_group_id: customerGroupId,
                customer_segment_cd: customerSegmentCd
              };

              if (policyNumber) {
                eventWithAdditionalProps = {
                  ...eventWithAdditionalProps,
                  partner_id: policyPartnerId,
                  policy_source_system: sourceSystem,
                  product_type: productType,
                  product_line: productLine,
                  account_type: accountType
                };
              }

              if (options.event?.event === 'internal_link_click') {
                eventWithAdditionalProps = {
                  ...eventWithAdditionalProps,
                  timestamp: new Date().toISOString()
                };
              }

              googleAnalyticsService.sendAnalytic(eventWithAdditionalProps);
            }
          }
          // send DD action if event or page
          if (!!options?.event || !!options?.page) {
            datadogService.sendActionWithAnalytic(options);
          }

          return actions.length > 0 ? actions : of();
        }
      )
    );
  },
  { functional: true }
);
